import { useEffect } from 'react'

import { format } from 'date-fns'

import { jerseyUnlocodeRoutes } from '@dfds-pax/utils'

import { isSogRoute } from '../../Utils/sog-helpers'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { getBaseApi } from '../../Utils/common'
import { fetcher } from '../../Utils/fetcher'

import { TravelDatesResponse } from './traveldates-types'

export function useLoadTravelDatesIntoContext(currency: string, offerCode: string) {
  const { dispatch, state } = useTravelSearchContext()

  const baseApi = getBaseApi()
  const routeCode = state.selectedRoute?.routeCode
  const salesOwner = state.selectedRoute?.salesOwner
  const unloCode = routeCode ?? ''
  const offerOrProductCode = state.routesApiData?.offerOrProductCode || offerCode || ''

  const buildParams = ({ direction, outboundDate }: { direction: 'outbound' | 'homebound'; outboundDate?: string }) => {
    const params = new URLSearchParams()
    params.append('direction', direction)
    params.append('salesOwner', salesOwner?.toString() ?? '')
    params.append('numberOfAdults', state.selectedPassengers?.adults?.toString() ?? '1')
    params.append('vehicleType', state.selectedVehicle?.code ?? '')
    state.selectedVehicle?.trailer?.code && params.append('trailerType', state.selectedVehicle.trailer.code)
    state.selectedPassengers?.pets && params.append('numberOfPets', state.selectedPassengers.pets.toString())
    state.selectedPassengers?.childrenAges?.forEach((age) => params.append('chdAges', age.toString()))
    params.append('route', unloCode ?? '')
    state.routesApiData?.offerOrProductCode && params.append('offerOrProductCode', state.routesApiData?.offerOrProductCode)

    if (!state.routesApiData?.offerOrProductCode && offerCode !== '') params.append('offerOrProductCode', offerCode)

    if (outboundDate) {
      params.append('outboundDate', outboundDate)
    }

    return params
  }

  // Fetch outbound dates when route / vehicle / passengers / offercode changes, and when the SOG cutover decision changes (dev/test).
  useEffect(() => {
    const outboundDateStr = state.selectedDates?.outboundDate ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd') : undefined
    if (
      !routeCode ||
      !salesOwner ||
      (!isSogRoute(routeCode, outboundDateStr) &&
        (!unloCode || !state.selectedVehicle?.code || state.vehicleApiLoading || !state.selectedPassengers))
    )
      return

    const fetchData = async () => {
      dispatch({ payload: true, type: 'SET_TRAVEL_DATES_API_LOADING' })

      const params = buildParams({ direction: 'outbound' })
      if (state.isDayTrip && state.showDayTripOffer) {
        params.set('offerOrProductCode', state.dayTripOfferCode)
      }

      try {
        const outboundUrl = isSogRoute(routeCode, outboundDateStr)
          ? `${baseApi}/available-travel-dates-frs?routeCode=${routeCode}&currencyCode=${currency}`
          : `${baseApi}/available-travel-dates?${params.toString()}`

        const [outboundData] = await Promise.all([fetcher<TravelDatesResponse>(outboundUrl)])

        dispatch({ payload: outboundData, type: 'SET_OUTBOUND_TRAVEL_DATES' })
      } catch (error) {
        console.error('Failed to fetch travel dates', error)
      } finally {
        dispatch({ payload: false, type: 'SET_TRAVEL_DATES_API_LOADING' })
      }
    }

    // Fetch daytrip travel dates (unless FRS route)
    const fetchDaytrip = async () => {
      if (!jerseyUnlocodeRoutes.includes(routeCode)) {
        dispatch({ show: false, type: 'SET_SHOW_DAY_TRIP_OFFER' })
        dispatch({ isDayTrip: false, type: 'SET_DAY_TRIP' })
        return
      }
      dispatch({ payload: true, type: 'SET_TRAVEL_DATES_API_LOADING' })
      try {
        const params = buildParams({ direction: 'outbound' })
        params.set('offerOrProductCode', state.dayTripOfferCode)
        params.set('isDayTrip', 'true')
        const daytripUrl = `${baseApi}/available-travel-dates?${params.toString()}`
        const daytripData = await fetcher<TravelDatesResponse>(daytripUrl)
        dispatch({ payload: daytripData, type: 'SET_DAYTRIP_TRAVEL_DATES' })
        if (daytripData.offerDates && daytripData.offerDates.length > 0) {
          dispatch({ show: true, type: 'SET_SHOW_DAY_TRIP_OFFER' })
        } else {
          dispatch({ show: false, type: 'SET_SHOW_DAY_TRIP_OFFER' })
          dispatch({ isDayTrip: false, type: 'SET_DAY_TRIP' })
        }
      } catch (error) {
        console.error('Failed to fetch daytrip travel dates', error)
      } finally {
        dispatch({ payload: false, type: 'SET_TRAVEL_DATES_API_LOADING' })
      }
    }

    fetchData()
    if (!offerCode || offerCode.length === 0 || offerCode === state.dayTripOfferCode) fetchDaytrip()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routeCode,
    salesOwner,
    unloCode,
    state.selectedPassengers,
    state.selectedVehicle?.code,
    offerOrProductCode,
    currency,
    state.vehicleApiLoading,
    state.dayTripOfferCode,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isSogRoute(routeCode ?? '', state.selectedDates?.outboundDate ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd') : undefined),
  ])

  // Refetch ONLY homebound when outbound date changes
  useEffect(() => {
    const outboundDateStr = state.selectedDates?.outboundDate ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd') : undefined
    if (
      !routeCode ||
      !salesOwner ||
      (!isSogRoute(routeCode, outboundDateStr) && !unloCode) ||
      !state.selectedDates.outboundDate ||
      state.isOneWay
    )
      return

    const fetchHomebound = async () => {
      dispatch({ payload: true, type: 'SET_TRAVEL_DATES_API_LOADING' })

      try {
        const homeboundUrl = isSogRoute(routeCode, outboundDateStr)
          ? `${baseApi}/available-travel-dates-frs?routeCode=${routeCode}&currencyCode=${currency}`
          : `${baseApi}/available-travel-dates?${buildParams({
              direction: 'homebound',
              outboundDate: state.selectedDates?.outboundDate
                ? format(state.selectedDates.outboundDate, 'yyyy-MM-dd')
                : format(new Date(), 'yyyy-MM-dd'),
            }).toString()}`

        const homeboundData = await fetcher<TravelDatesResponse>(homeboundUrl)
        dispatch({
          payload: homeboundData,
          type: 'SET_HOMEBOUND_TRAVEL_DATES',
        })
      } catch (error) {
        console.error('Failed to fetch homebound travel dates', error)
      } finally {
        dispatch({ payload: false, type: 'SET_TRAVEL_DATES_API_LOADING' })
      }
    }

    fetchHomebound()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedDates.outboundDate, routeCode, salesOwner, unloCode, offerOrProductCode, currency, state.isOneWay])
}
